import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { ClinicProvider, useClinic } from '@/contexts/ClinicContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import MainLayout from '@/components/layout/MainLayout';

// Landing Page
import LandingPage from '@/pages/LandingPage';

// Auth & Gateway Pages
import HospitalFinderPage from '@/pages/auth/HospitalFinderPage';
import LoginPage from '@/pages/auth/LoginPage';
import ClinicSelectionPage from '@/pages/auth/ClinicSelectionPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';

// Dashboard
import DashboardPage from '@/pages/dashboard/DashboardPage';

// Patients
import PatientListPage from '@/pages/patients/PatientListPage';
import PatientFormPage from '@/pages/patients/PatientFormPage';
import PatientDetailPage from '@/pages/patients/PatientDetailPage';

// Appointments
import AppointmentListPage from '@/pages/appointments/AppointmentListPage';
import AppointmentFormPage from '@/pages/appointments/AppointmentFormPage';
import AppointmentDetailPage from '@/pages/appointments/AppointmentDetailPage';

// Consultations
import ConsultationListPage from '@/pages/consultations/ConsultationListPage';
import ConsultationFormPage from '@/pages/consultations/ConsultationFormPage';

// Doctors
import DoctorListPage from '@/pages/doctors/DoctorListPage';
import DoctorFormPage from '@/pages/doctors/DoctorFormPage';
import DoctorDetailPage from '@/pages/doctors/DoctorDetailPage';

// Reports
import ReportsPage from '@/pages/reports/ReportsPage';

// Payments
import PaymentDashboardPage from '@/pages/payments/PaymentDashboardPage';

// Settings
import SettingsPage from '@/pages/settings/SettingsPage';

import '@/App.css';

// Component to synchronize URL params with ClinicContext
const ScopedClinicSync = () => {
  const { hospitalId, clinicId } = useParams();
  const { hospitalId: currentHospId, activeClinicId, setHospitalId, setActiveClinicId } = useClinic();

  React.useEffect(() => {
    if (hospitalId && hospitalId !== currentHospId) {
      setHospitalId(hospitalId);
    }
    if (clinicId && clinicId !== activeClinicId) {
      setActiveClinicId(clinicId);
    }
  }, [hospitalId, clinicId, currentHospId, activeClinicId, setHospitalId, setActiveClinicId]);

  return <MainLayout />;
};

// Dynamically redirect flat routes using the active tenant context (NO hardcoded hosp-1 or c-opd)
const DynamicTenantRedirect = ({ subpath = 'dashboard' }) => {
  const { hospitalId, activeClinicId } = useClinic();

  const hosp =
    hospitalId ||
    (typeof window !== 'undefined' ? sessionStorage.getItem('pulseflow_hospital_id') : null);
  const cln =
    activeClinicId ||
    (typeof window !== 'undefined' ? sessionStorage.getItem('pulseflow_clinic_id') : null);

  if (!hosp) {
    return <Navigate to="/login" replace />;
  }

  if (!cln) {
    return <Navigate to={`/hospital/${hosp}/select-clinic`} replace />;
  }

  return <Navigate to={`/hospital/${hosp}/clinic/${cln}/${subpath.replace(/^\//, '')}`} replace />;
};

function App() {
  return (
    <AuthProvider>
      <ClinicProvider>
        <BrowserRouter>
          <Routes>
            {/* Public & Authentication Entry Routes */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/find-hospital" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/hospital/:hospitalId/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Clinic Selection (Post-Auth, Pre-Dashboard) */}
            <Route
              path="/hospital/:hospitalId/select-clinic"
              element={
                <ProtectedRoute>
                  <ClinicSelectionPage />
                </ProtectedRoute>
              }
            />

            {/* Authenticated & Scoped Routes: /hospital/:hospitalId/clinic/:clinicId/* */}
            <Route
              path="/hospital/:hospitalId/clinic/:clinicId"
              element={
                <ProtectedRoute>
                  <ScopedClinicSync />
                </ProtectedRoute>
              }
            >
              {/* Root redirect to dashboard */}
              <Route index element={<Navigate to="dashboard" replace />} />

              {/* Dashboard */}
              <Route
                path="dashboard"
                element={
                  <ProtectedRoute requiredPermission="DASHBOARD_OVERVIEW" requiredFeature="dashboard">
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />

              {/* Patients Module */}
              <Route
                path="patients"
                element={
                  <ProtectedRoute requiredPermission="PATIENT_READ" requiredFeature="patients">
                    <PatientListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="patients/new"
                element={
                  <ProtectedRoute requiredPermission="PATIENT_WRITE" requiredFeature="patients">
                    <PatientFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="patients/:id"
                element={
                  <ProtectedRoute requiredPermission="PATIENT_READ" requiredFeature="patients">
                    <PatientDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="patients/:id/edit"
                element={
                  <ProtectedRoute requiredPermission="PATIENT_WRITE" requiredFeature="patients">
                    <PatientFormPage />
                  </ProtectedRoute>
                }
              />

              {/* Appointments Module */}
              <Route
                path="appointments"
                element={
                  <ProtectedRoute requiredPermission="APPOINTMENT_READ" requiredFeature="appointments">
                    <AppointmentListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="appointments/new"
                element={
                  <ProtectedRoute requiredPermission="APPOINTMENT_WRITE" requiredFeature="appointments">
                    <AppointmentFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="appointments/:id"
                element={
                  <ProtectedRoute requiredPermission="APPOINTMENT_READ" requiredFeature="appointments">
                    <AppointmentDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* Doctor Quick Aliases */}
              <Route
                path="my-appointments"
                element={
                  <ProtectedRoute requiredPermission="APPOINTMENT_READ" requiredFeature="appointments">
                    <AppointmentListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="my-schedule"
                element={
                  <ProtectedRoute requiredPermission="DASHBOARD_OVERVIEW" requiredFeature="dashboard">
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />

              {/* Consultations Module */}
              <Route
                path="consultations"
                element={
                  <ProtectedRoute requiredAnyPermissions={['CONSULTATION_READ', 'OPD_QUEUE_MANAGE']} requiredFeature="consultations">
                    <ConsultationListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="consultations/new"
                element={
                  <ProtectedRoute requiredPermission="CONSULTATION_WRITE" requiredFeature="consultations">
                    <ConsultationFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="consultations/:id"
                element={
                  <ProtectedRoute requiredPermission="CONSULTATION_WRITE" requiredFeature="consultations">
                    <ConsultationFormPage />
                  </ProtectedRoute>
                }
              />

              {/* Doctors Directory & Profile */}
              <Route
                path="doctors"
                element={
                  <ProtectedRoute requiredAnyPermissions={['STAFF_MANAGE', 'USER_READ']} requiredFeature="doctors">
                    <DoctorListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="doctors/new"
                element={
                  <ProtectedRoute requiredPermission="STAFF_MANAGE" requiredFeature="doctors">
                    <DoctorFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="doctors/:id"
                element={
                  <ProtectedRoute requiredAnyPermissions={['STAFF_MANAGE', 'USER_READ']} requiredFeature="doctors">
                    <DoctorDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="doctors/:id/edit"
                element={
                  <ProtectedRoute requiredPermission="STAFF_MANAGE" requiredFeature="doctors">
                    <DoctorFormPage />
                  </ProtectedRoute>
                }
              />

              {/* Reports & Analytics */}
              <Route
                path="reports"
                element={
                  <ProtectedRoute requiredPermission="REPORT_READ" requiredFeature="reports">
                    <ReportsPage />
                  </ProtectedRoute>
                }
              />

              {/* Payments & Revenue Module */}
              <Route
                path="payments"
                element={
                  <ProtectedRoute requiredFeature="payments">
                    <PaymentDashboardPage />
                  </ProtectedRoute>
                }
              />

              {/* Settings */}
              <Route
                path="settings"
                element={
                  <ProtectedRoute requiredPermission="FEATURE_SETTINGS_MANAGE">
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Flat route dynamic redirects using current tenant context (NO hosp-1 or c-opd) */}
            <Route path="/dashboard" element={<DynamicTenantRedirect subpath="dashboard" />} />
            <Route path="/patients/new" element={<DynamicTenantRedirect subpath="patients/new" />} />
            <Route path="/patients/:id" element={<DynamicTenantRedirect subpath="patients" />} />
            <Route path="/appointments" element={<DynamicTenantRedirect subpath="appointments" />} />
            <Route path="/appointments/new" element={<DynamicTenantRedirect subpath="appointments/new" />} />
            <Route path="/consultations" element={<DynamicTenantRedirect subpath="consultations" />} />
            <Route path="/doctors" element={<DynamicTenantRedirect subpath="doctors" />} />
            <Route path="/reports" element={<DynamicTenantRedirect subpath="reports" />} />
            <Route path="/payments" element={<DynamicTenantRedirect subpath="payments" />} />
            <Route path="/settings" element={<DynamicTenantRedirect subpath="settings" />} />

            {/* Legacy /app/* redirects */}
            <Route path="/app" element={<DynamicTenantRedirect subpath="dashboard" />} />
            <Route path="/app/*" element={<DynamicTenantRedirect subpath="dashboard" />} />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ClinicProvider>
    </AuthProvider>
  );
}

export default App;
